import React, {
  useState,
  useEffect,
} from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Autocomplete,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as AchievementIcon,
  Person as PersonIcon,
  CloudDownload as SeedIcon,
  Assessment as StatsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  achievementsService,
} from '../../services/achievements';
import {
  apiService,
} from '../../services/api';

const AchievementManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [achievements, setAchievements] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [, setAwardDialog] = useState(false);
  const [seedDialog, setSeedDialog] = useState(false);
  
  // Form states
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'events',
    rarity: 'common',
    points: 10,
    icon: 'event',
    criteria_type: '',
    criteria_value: null,
    auto_unlock: true,
    order: 1
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [awardAchievementId, setAwardAchievementId] = useState('');

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  useEffect(() => {
    // Load users when award tab is active
    if (tabValue === 2) {
      loadAvailableUsers();
    }
  }, [tabValue]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        // Load achievements
        const result = await achievementsService.getAchievements();
        if (result.success) {
          setAchievements(result.data);
        } else {
          setError(result.error);
        }
      } else if (tabValue === 1) {
        // Load statistics
        const result = await achievementsService.getStatistics();
        if (result.success) {
          setStatistics(result.data);
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const result = await apiService.getUsers();
      if (result.success) {
        setAvailableUsers(result.data);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setAvailableUsers([]);
    }
  };

  const handleCreateAchievement = async () => {
    try {
      const result = await achievementsService.createAchievement(formData);
      if (result.success) {
        setSuccess('Achievement created successfully');
        setCreateDialog(false);
        loadData();
        resetForm();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to create achievement');
    }
  };

  const handleUpdateAchievement = async () => {
    if (!selectedAchievement) return;
    
    try {
      const result = await achievementsService.updateAchievement(
        selectedAchievement.id || selectedAchievement._id,
        formData
      );
      if (result.success) {
        setSuccess('Achievement updated successfully');
        setEditDialog(false);
        loadData();
        resetForm();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update achievement');
    }
  };

  const handleDeleteAchievement = async (achievementId) => {
    if (!window.confirm('Are you sure you want to delete this achievement?')) return;
    
    try {
      const result = await achievementsService.deleteAchievement(achievementId);
      if (result.success) {
        setSuccess('Achievement deleted successfully');
        loadData();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to delete achievement');
    }
  };

  const handleAwardAchievement = async () => {
    if (!selectedUser || !awardAchievementId) {
      setError('Please select both a user and an achievement');
      return;
    }
    
    try {
      // Get current admin user ID from localStorage or context
      const adminUserId = localStorage.getItem('user_id') || '';
      const userId = selectedUser.id || selectedUser._id;
      
      const result = await achievementsService.awardAchievement(
        userId,
        awardAchievementId,
        adminUserId
      );
      
      if (result.success) {
        setSuccess(`Achievement awarded to ${selectedUser.username} successfully`);
        setAwardDialog(false);
        setSelectedUser(null);
        setAwardAchievementId('');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to award achievement');
    }
  };

  const handleSeedAchievements = async (force = false) => {
    try {
      const result = await achievementsService.seedAchievements(force);
      if (result.success) {
        setSuccess(result.message || 'Achievements seeded successfully');
        setSeedDialog(false);
        loadData();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to seed achievements');
    }
  };

  const openEditDialog = (achievement) => {
    setSelectedAchievement(achievement);
    setFormData({
      title: achievement.title,
      description: achievement.description,
      category: achievement.category,
      rarity: achievement.rarity,
      points: achievement.points,
      icon: achievement.icon,
      criteria_type: achievement.criteria_type,
      criteria_value: achievement.criteria_value,
      auto_unlock: achievement.auto_unlock,
      order: achievement.order
    });
    setEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'events',
      rarity: 'common',
      points: 10,
      icon: 'event',
      criteria_type: '',
      criteria_value: null,
      auto_unlock: true,
      order: 1
    });
    setSelectedAchievement(null);
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: '#9d9d9d',
      rare: '#0070dd',
      epic: '#a335ee',
      legendary: '#ff8000'
    };
    return colors[rarity] || colors.common;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AchievementIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Achievement Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<SeedIcon />}
            onClick={() => setSeedDialog(true)}
          >
            Seed Achievements
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Create Achievement
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Achievements List" icon={<AchievementIcon />} iconPosition="start" />
          <Tab label="Statistics" icon={<StatsIcon />} iconPosition="start" />
          <Tab label="Award Achievements" icon={<PersonIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Achievements List Tab */}
          {tabValue === 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                All Achievements ({achievements.length})
              </Typography>
              <Divider sx={{ my: 2 }} />
              <List>
                {achievements.map((achievement) => (
                  <ListItem
                    key={achievement.id || achievement._id}
                    sx={{
                      borderLeft: `4px solid ${getRarityColor(achievement.rarity)}`,
                      mb: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {achievement.title}
                          </Typography>
                          <Chip label={achievement.rarity} size="small" sx={{ bgcolor: getRarityColor(achievement.rarity), color: 'white' }} />
                          <Chip label={achievement.category} size="small" variant="outlined" />
                          <Chip label={`${achievement.points} pts`} size="small" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {achievement.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Criteria: {achievement.criteria_type} {achievement.criteria_value && `(${achievement.criteria_value})`} | 
                            {achievement.auto_unlock ? ' Auto-unlock' : ' Manual'}
                          </Typography>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => openEditDialog(achievement)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteAchievement(achievement.id || achievement._id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Statistics Tab */}
          {tabValue === 1 && statistics && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Overview</Typography>
                    <Typography variant="body2">Total Achievements: {statistics.total_achievements}</Typography>
                    <Typography variant="body2">Total Unlocks: {statistics.total_unlocks}</Typography>
                    <Typography variant="body2">Completion Rate: {statistics.completion_rate}%</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Rarity Distribution</Typography>
                    {Object.entries(statistics.rarity_distribution).map(([rarity, count]) => (
                      <Typography key={rarity} variant="body2">
                        {rarity}: {count}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Top Achievers</Typography>
                    <List>
                      {statistics.top_achievers.map((achiever, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={achiever.username}
                            secondary={`${achiever.total_points} points | ${achiever.unlocked_count} achievements`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Award Achievements Tab */}
          {tabValue === 2 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Manually Award Achievement
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Search for a user and select an achievement to award manually
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={availableUsers}
                    getOptionLabel={(option) => `${option.username} (${option.email})`}
                    value={selectedUser}
                    onChange={(event, newValue) => setSelectedUser(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select User"
                        placeholder="Search by username or email"
                        fullWidth
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1">{option.username}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Achievement</InputLabel>
                    <Select
                      value={awardAchievementId}
                      onChange={(e) => setAwardAchievementId(e.target.value)}
                      label="Achievement"
                    >
                      {achievements.map((achievement) => (
                        <MenuItem key={achievement.id || achievement._id} value={achievement.id || achievement._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={achievement.rarity} 
                              size="small" 
                              sx={{ bgcolor: getRarityColor(achievement.rarity), color: 'white' }} 
                            />
                            {achievement.title} ({achievement.points} pts)
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {selectedUser && awardAchievementId && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You are about to award <strong>{achievements.find(a => (a.id || a._id) === awardAchievementId)?.title}</strong> to <strong>{selectedUser.username}</strong>
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleAwardAchievement}
                    disabled={!selectedUser || !awardAchievementId}
                    startIcon={<AchievementIcon />}
                  >
                    Award Achievement
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Achievement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="events">Events</MenuItem>
                  <MenuItem value="tournaments">Tournaments</MenuItem>
                  <MenuItem value="games">Games</MenuItem>
                  <MenuItem value="trackmania">TrackMania</MenuItem>
                  <MenuItem value="skill">Skills</MenuItem>
                  <MenuItem value="social">Social</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Rarity</InputLabel>
                <Select
                  value={formData.rarity}
                  onChange={(e) => setFormData({ ...formData, rarity: e.target.value })}
                  label="Rarity"
                >
                  <MenuItem value="common">Common</MenuItem>
                  <MenuItem value="rare">Rare</MenuItem>
                  <MenuItem value="epic">Epic</MenuItem>
                  <MenuItem value="legendary">Legendary</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Points"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Order"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Criteria Type"
                value={formData.criteria_type}
                onChange={(e) => setFormData({ ...formData, criteria_type: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Criteria Value"
                value={formData.criteria_value || ''}
                onChange={(e) => setFormData({ ...formData, criteria_value: parseInt(e.target.value) || null })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateAchievement}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Achievement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Points"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Order"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateAchievement}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seed Dialog */}
      <Dialog open={seedDialog} onClose={() => setSeedDialog(false)}>
        <DialogTitle>Seed Achievement Definitions</DialogTitle>
        <DialogContent>
          <Typography>
            This will create 50 predefined achievements for your esports platform.
            If achievements already exist, they will not be duplicated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeedDialog(false)}>Cancel</Button>
          <Button onClick={() => handleSeedAchievements(false)} variant="contained">
            Seed Achievements
          </Button>
          <Button onClick={() => handleSeedAchievements(true)} variant="outlined" color="error">
            Force Reseed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AchievementManagement;

